import type {
  PrimaryColorTokens,
  SecondaryColorTokens,
  TertiaryColorTokens,
  TextInteractiveColorTokens,
  CautionColorTokens,
  AccentGreenColorTokens,
  AccentRedColorTokens,
  InfoColorTokens,
  BorderRadiusTokens,
  ThemeTypographySubset,
  BrandFields,
  ProtectedColorThemeSettingFields,
  FontFields,
  ProtectedFontThemeSettingFields,
  ProtectedTopLevelColorTokens,
  BorderRadiusThemeSettingFields,
  FillSurfaceColorTokens,
  FwcDarkSurfaceTokens,
  FwcTableSurfaceTokens,
  FwcPrimaryColorTokens,
  FwcSecondaryColorTokens,
  FwcTertiaryColorTokens,
  FwcTextInteractiveColorTokens,
  FwcAccentRedColorTokens,
  FwcAccentGreenColorTokens,
  FwcCautionColorTokens,
  FwcInfoColorTokens,
  FwcAccentOrangeColorTokens,
  FwcAccentPurpleColorTokens,
  FwcBorderRadiusTokens,
  AvailableTokens,
  StyleField,
  VariantSet,
  FontFieldValue,
} from '../types';
import { TOKEN_VARIANT_CONFIGS, SINGLE_VARIANT } from '../types';
import {
  deriveColorVariants,
  getBestTextColor,
  getDisabledTextColor,
  hexToHsl,
  hslToHex,
  deriveAlertSubtleColor,
} from './color';

// Colors
export function derivePrimaryColorVariants(
  baseColor: string
): PrimaryColorTokens & FwcPrimaryColorTokens {
  const variants = deriveColorVariants(baseColor);
  const textColor = getBestTextColor(baseColor);
  const disabledTextColor = getDisabledTextColor(baseColor);

  const baseTokens = {
    'fill-primary-default': variants.default,
    'fill-primary-hover': variants.hover,
    'fill-primary-pressed': variants.pressed,
    'fill-primary-disabled': variants.disabled,
    'border-primary-default': baseColor,
    'text-primary-default': textColor,
    'icon-primary-default': textColor,
    'text-primary-disabled': disabledTextColor,
  } satisfies PrimaryColorTokens;

  return {
    ...baseTokens,
    'fwc-button-fill-primary-default': variants.default,
    'fwc-button-fill-primary-hover': variants.hover,
    'fwc-button-fill-primary-pressed': variants.pressed,
    'fwc-button-fill-primary-disabled': variants.disabled,
    'fwc-button-border-primary-default': baseColor,
    'fwc-button-border-primary-disabled': 'rgba(20,20,20,0)',
    'fwc-button-text-primary-default': textColor,
    'fwc-button-text-primary-disabled': disabledTextColor,
  };
}

export function deriveSecondaryColorVariants(
  baseColor: string
): SecondaryColorTokens & FwcSecondaryColorTokens {
  const variants = deriveColorVariants(baseColor);
  const disabledTextColor = getDisabledTextColor(baseColor);

  const baseTokens = {
    'fill-secondary-default': variants.default, // keeping this as full color so other teams can use this secondary color for other purposes besides buttons
    'fill-secondary-default-alt': 'transparent',
    'fill-secondary-hover': variants.hover,
    'fill-secondary-hover-alt': variants.subtle,
    'fill-secondary-pressed': variants.pressed,
    'fill-secondary-pressed-alt': variants.pressedSubtle,
    'fill-secondary-disabled': 'transparent',
    'fill-secondary-disabled-alt': variants.disabled,
    'border-secondary-default': baseColor,
    'border-secondary-default-alt': baseColor,
    'text-secondary-default': variants.pressed,
    'text-secondary-disabled': disabledTextColor,
  } satisfies SecondaryColorTokens;

  return {
    ...baseTokens,
    'fwc-button-fill-secondary-default': variants.default,
    'fwc-button-fill-secondary-default-alt': 'transparent',
    'fwc-button-fill-secondary-hover': variants.hover,
    'fwc-button-fill-secondary-hover-alt': variants.subtle,
    'fwc-button-fill-secondary-pressed': variants.pressed,
    'fwc-button-fill-secondary-pressed-alt': variants.pressedSubtle,
    'fwc-button-fill-secondary-disabled': 'transparent',
    'fwc-button-fill-secondary-disabled-alt': variants.disabled,
    'fwc-button-border-secondary-default': baseColor,
    'fwc-button-border-secondary-default-alt': baseColor,
    'fwc-button-border-secondary-disabled': 'rgba(0,0,0,0)',
    'fwc-button-border-secondary-disabled-alt': 'rgba(20,20,20,0)',
    'fwc-button-text-secondary-default': variants.pressed,
    'fwc-button-text-secondary-disabled': disabledTextColor,
  };
}

export function deriveTertiaryColorVariants(
  baseColor: string
): TertiaryColorTokens & FwcTertiaryColorTokens {
  const variants = deriveColorVariants(baseColor);
  const textColor = getBestTextColor(baseColor);
  const disabledTextColor = getDisabledTextColor(baseColor);

  const baseTokens = {
    'fill-tertiary-default': variants.default,
    'fill-transitional-tertiary-default': variants.default,
    'fill-transitional-tertiary-hover': variants.hover,
    'fill-transitional-tertiary-pressed': variants.pressed,
    'fill-tertiary-hover': variants.hover,
    'fill-tertiary-pressed': variants.pressed,
    'fill-tertiary-disabled': variants.disabled,
    'border-tertiary-default': baseColor,
    // The token below is used on tertiary-light versions of buttons
    'text-tertiary-default': textColor,
    'text-tertiary-disabled': disabledTextColor,
  } satisfies TertiaryColorTokens;

  return {
    ...baseTokens,
    'fwc-button-fill-tertiary-default': variants.default,
    'fwc-button-fill-tertiary-hover': variants.hover,
    'fwc-button-fill-tertiary-pressed': variants.pressed,
    'fwc-button-fill-tertiary-disabled': variants.disabled,
    'fwc-button-border-tertiary-default': baseColor,
    'fwc-button-border-tertiary-disabled': 'rgba(0,0,0,0)',
    'fwc-button-text-tertiary-default': textColor,
    'fwc-button-text-tertiary-disabled': disabledTextColor,
  };
}

export function deriveSurfaceTokensFromStyle(
  style: StyleField
): Partial<
  FillSurfaceColorTokens & FwcDarkSurfaceTokens & FwcTableSurfaceTokens
> {
  if (style === 'dark') {
    return deriveDarkSurfaceOverrides();
  }
  return deriveLightSurfaceOverrides();
}

// Dark mode: rely on the Trellis dark theme as the base. Only override tokens
// where portals intentionally diverges from Trellis dark.
//
// foundations-theming-base/stable/* paths are non-public and banned by lint,
// so these are hardcoded from the trellis-dark theme.
const TRELLIS_DARK_SURFACE_DEFAULT = '#1F1F1F';
const TRELLIS_DARK_SURFACE_RAISED = '#292929';

function deriveDarkSurfaceOverrides(): Partial<
  FillSurfaceColorTokens & FwcDarkSurfaceTokens & FwcTableSurfaceTokens
> {
  const surfaceHsl = hexToHsl(TRELLIS_DARK_SURFACE_DEFAULT);
  const surfaceVariants = deriveColorVariants(TRELLIS_DARK_SURFACE_DEFAULT);

  const BORDER_LIGHTNESS_CONFIG = {
    maxSaturation: 10,
    subtle: { offset: 8, darkMin: 20 },
    default: { offset: 16, darkMin: 30 },
    hover: { offset: 32, darkMin: 45 },
    emphasis: { offset: 22, darkMin: 34 },
  };

  const borderSubtle = hslToHex({
    ...surfaceHsl,
    l: Math.max(
      Math.min(surfaceHsl.l + BORDER_LIGHTNESS_CONFIG.subtle.offset, 100),
      BORDER_LIGHTNESS_CONFIG.subtle.darkMin
    ),
    s: Math.min(surfaceHsl.s, BORDER_LIGHTNESS_CONFIG.maxSaturation),
  });

  const borderDefault = hslToHex({
    ...surfaceHsl,
    l: Math.max(
      Math.min(surfaceHsl.l + BORDER_LIGHTNESS_CONFIG.default.offset, 100),
      BORDER_LIGHTNESS_CONFIG.default.darkMin
    ),
    s: Math.min(surfaceHsl.s, BORDER_LIGHTNESS_CONFIG.maxSaturation),
  });

  const borderHover = hslToHex({
    ...surfaceHsl,
    l: Math.max(
      Math.min(surfaceHsl.l + BORDER_LIGHTNESS_CONFIG.hover.offset, 100),
      BORDER_LIGHTNESS_CONFIG.hover.darkMin
    ),
    s: Math.min(surfaceHsl.s, BORDER_LIGHTNESS_CONFIG.maxSaturation),
  });

  const TABLE_HEADER_LIGHTNESS_OFFSET = 5;
  const tableHeaderDefault = hslToHex({
    ...surfaceHsl,
    l: Math.min(surfaceHsl.l + TABLE_HEADER_LIGHTNESS_OFFSET, 100),
  });
  const tableHeaderVariants = deriveColorVariants(tableHeaderDefault);

  // Header→body divider. trellis-dark ships this near-white (#F5F5F5), a harsh
  // line on dark; derive a mid-gray instead.
  const tableDividerEmphasis = hslToHex({
    ...surfaceHsl,
    l: Math.max(
      Math.min(surfaceHsl.l + BORDER_LIGHTNESS_CONFIG.emphasis.offset, 100),
      BORDER_LIGHTNESS_CONFIG.emphasis.darkMin
    ),
    s: Math.min(surfaceHsl.s, BORDER_LIGHTNESS_CONFIG.maxSaturation),
  });

  const darkTextColor = getBestTextColor(TRELLIS_DARK_SURFACE_DEFAULT);

  return {
    // Border tokens derived from the dark surface base. Trellis dark leaves
    // border-transitional-core-subtle at a light value (#F5F5F5) which renders
    // as a bright outline on dark popovers; the others are overridden for
    // consistent contrast against the dark surface.
    'border-core-default': borderDefault,
    'border-core-subtle': borderSubtle,
    'border-core-disabled': '#4a4a4a', // darker than Trellis #333333 for better visibility on dark surfaces

    'border-core-hover': borderHover,
    // Row separators. (The vertical header dividers that also read this token
    // are removed by width — see defaultBorderWidthOverrides.)
    'border-transitional-core-subtle': borderSubtle,
    // trellis-dark maps border-transitional-core-subtle-alt to #F5F5F5 (near-white), producing
    // a stark visible border on Modal/Dialog in dark mode. Use the same subtle value as other
    // dark surface borders so it blends with the dark background.
    'border-transitional-core-subtle-alt': borderSubtle,

    // Dark field backgrounds (trellis-dark gap, uses same white #FFFFFF values as trellis-light)
    'fill-field-default': TRELLIS_DARK_SURFACE_RAISED,
    'fill-field-default-alt': surfaceVariants.hover,
    'fill-field-disabled': '#2a2a2a', // darker than Trellis #333333 to distinguish disabled from default field
    'fill-field-hover': surfaceVariants.hover,

    // Dark-appropriate table headers (trellis-dark has light-themed values)
    'specialty-table-header-default': tableHeaderDefault,
    'specialty-table-header-hover': tableHeaderVariants.hover,
    'specialty-table-header-pressed': tableHeaderVariants.pressed,
    'fwc-table-divider-color-emphasis': tableDividerEmphasis,

    // trellis-dark defines fill-transparent-default as #FFFFFF (should be transparent)
    'fill-transparent-default': 'rgba(255,255,255,0)',
    // trellis-dark defines specialty-scrim as #141414 (fully opaque), unlike trellis-light
    // which uses rgba(20,20,20,0.46). Override so modal overlays remain semi-transparent.
    'specialty-scrim': 'rgba(20,20,20,0.5)',
    // trellis-dark maps fill-inverse-hover-transparent to #FFFFFF (solid white), unlike
    // trellis-light which uses rgba(20,20,20,0.08). This token controls the close button
    // hover background — override to a subtle white overlay so the icon remains visible.
    'fill-inverse-hover-transparent': 'rgba(255,255,255,0.1)',
    // used for PanelHeaders which expects two colors for a
    // gradient background-image. These colors will a subtle gradient
    // that I think is appropriate until we get more customer feedback.
    'fill-transitional-modal-gradient-color-1': surfaceVariants.default,
    'fill-transitional-modal-gradient-color-2': surfaceVariants.default,
    // text-inverse-default is the Dialog/PanelHeader title text color, rendered on
    // the modal header gradient. In dark mode the gradient is overridden to dark
    // surface colors above, so derive the title text from the dark surface (yields
    // light text). Trellis dark base value: #F5F5F5.
    'text-inverse-default': darkTextColor,

    // fwc-button text tokens are #141414 in trellis-dark (copied from trellis-light
    // without inversion), making them invisible on dark surfaces.
    'fwc-button-text-core-default': darkTextColor,
    'fwc-button-text-secondary-default': darkTextColor,
    'fwc-button-text-tertiary-default': darkTextColor,
    'fwc-link-text-core-default': darkTextColor,

    // fwc-button fill tokens are #FFFFFF in trellis-dark — used by Button
    // use="form" (Select's default anchor) on dark surfaces.
    'fwc-button-fill-surface-default': TRELLIS_DARK_SURFACE_DEFAULT,
    'fwc-button-fill-surface-raised': TRELLIS_DARK_SURFACE_RAISED,
    'fwc-button-fill-field-default-alt': TRELLIS_DARK_SURFACE_RAISED,
    'fwc-button-border-core-default': borderDefault,
    'fwc-button-border-core-hover': borderHover,

    // RadioInput/Checkbox — trellis-dark has #141414 for borders/text
    'fwc-radio-border-default': borderDefault,
    'fwc-radio-border-readonly': borderSubtle,
    'fwc-radio-fill-readonly-indicator': darkTextColor,
    'fwc-radio-text-readonly': darkTextColor,

    // SearchInput border
    'fwc-searchinput-border-default': borderDefault,

    // Toggle
    'fwc-toggle-border-readonly': '#4a4a4a',

    // Progress
    'fwc-progress-fill-empty': surfaceVariants.hover,
    'fwc-progress-text-default': darkTextColor,

    // Tag — text and default fills
    'fwc-tag-text-onfill': darkTextColor,
    'fwc-tag-text-subtle': darkTextColor,
    'fwc-tag-fill-default': TRELLIS_DARK_SURFACE_RAISED,
    'fwc-tag-fill-subtle': TRELLIS_DARK_SURFACE_RAISED,

    // TextInput
    'fwc-textinput-fill-readonly': TRELLIS_DARK_SURFACE_RAISED,
    'fwc-textinput-border-readonly': borderSubtle,

    // LoadingSpinner — ghost and misc variants
    'fwc-loadingspinner-ring-ghost': darkTextColor,
    'fwc-loadingspinner-indicator-ghost': darkTextColor,
    'fwc-loadingspinner-ring-tertiary': surfaceVariants.hover,
    'fwc-loadingspinner-indicator-tertiary': darkTextColor,
    'fwc-loadingspinner-ring-onfill': surfaceVariants.hover,
    'fwc-loadingspinner-indicator-onfill': darkTextColor,
    'fwc-loadingspinner-ring-primary': surfaceVariants.hover,
    'fwc-loadingspinner-indicator-primary': darkTextColor,

    // secondary-alt hover/pressed fills are variants.subtle/pressedSubtle which
    // compute to near-white tints (~96% lightness). Override to translucent
    // overlays that work on dark surfaces.
    'fwc-button-fill-secondary-hover-alt': 'rgba(255, 255, 255, 0.1)',
    'fwc-button-fill-secondary-pressed-alt': 'rgba(255, 255, 255, 0.15)',

    // secondary-ghost button (inverse tokens) — trellis-dark has #141414 text
    // and borders, identical to trellis-light. Override for dark surface contrast.
    'fwc-button-text-inverse-default': darkTextColor,
    'fwc-button-text-inverse-hover': darkTextColor,
    'fwc-button-text-inverse-pressed': darkTextColor,
    'fwc-button-text-inverse-disabled': '#666666',
    'fwc-button-border-inverse-default': darkTextColor,
    'fwc-button-border-inverse-disabled': '#4a4a4a',
    'fwc-button-fill-inverse-default': 'transparent',
    'fwc-button-fill-inverse-hover': 'rgba(255, 255, 255, 0.1)',
    'fwc-button-fill-inverse-pressed': 'rgba(255, 255, 255, 0.15)',
    'fwc-button-fill-inverse-disabled': 'rgba(255, 255, 255, 0.05)',
  };
}

// Light mode: override only tokens where portals intentionally diverges from
// Trellis light (subtler borders, WCAG AA disabled text, gray fields, custom
// accent neutral). All other surface tokens come from the Trellis light base.
const TRELLIS_LIGHT_SURFACE_DEFAULT = '#FFFFFF';

function deriveLightSurfaceOverrides(): Partial<
  FillSurfaceColorTokens & FwcTableSurfaceTokens
> {
  const surfaceHsl = hexToHsl(TRELLIS_LIGHT_SURFACE_DEFAULT);
  const surfaceVariants = deriveColorVariants(TRELLIS_LIGHT_SURFACE_DEFAULT);

  const BORDER_LIGHTNESS_CONFIG = {
    maxSaturation: 10,
    subtle: { offset: 8 },
    default: { offset: 16 },
    hover: { offset: 32 },
    emphasis: { offset: 28 },
  };

  const borderSubtle = hslToHex({
    ...surfaceHsl,
    l: Math.max(surfaceHsl.l - BORDER_LIGHTNESS_CONFIG.subtle.offset, 0),
    s: Math.min(surfaceHsl.s, BORDER_LIGHTNESS_CONFIG.maxSaturation),
  });

  const borderDefault = hslToHex({
    ...surfaceHsl,
    l: Math.max(surfaceHsl.l - BORDER_LIGHTNESS_CONFIG.default.offset, 0),
    s: Math.min(surfaceHsl.s, BORDER_LIGHTNESS_CONFIG.maxSaturation),
  });

  const borderHover = hslToHex({
    ...surfaceHsl,
    l: Math.max(surfaceHsl.l - BORDER_LIGHTNESS_CONFIG.hover.offset, 0),
    s: Math.min(surfaceHsl.s, BORDER_LIGHTNESS_CONFIG.maxSaturation),
  });

  const RAISED_LIGHTNESS_OFFSET = 3;
  const raisedSurface = hslToHex({
    ...surfaceHsl,
    l: Math.max(surfaceHsl.l - RAISED_LIGHTNESS_OFFSET, 0),
  });

  // Header→body divider. trellis-light ships this as a heavy #333333 line;
  // soften to a lighter gray.
  const tableDividerEmphasis = hslToHex({
    ...surfaceHsl,
    l: Math.max(surfaceHsl.l - BORDER_LIGHTNESS_CONFIG.emphasis.offset, 0),
    s: Math.min(surfaceHsl.s, BORDER_LIGHTNESS_CONFIG.maxSaturation),
  });

  return {
    // Portals uses subtler/lighter borders than Trellis light
    'border-core-default': borderDefault,
    'border-core-subtle': borderSubtle,
    'border-core-disabled': '#d1d1d1', // darker than Trellis #E6E6E6 for better visibility on light surfaces
    'border-core-hover': borderHover,
    'border-transitional-core-subtle': borderDefault,
    'fwc-table-divider-color-emphasis': tableDividerEmphasis,

    // Slightly gray raised surface (Trellis uses pure white)
    'fill-surface-raised': raisedSurface,

    // Gray field backgrounds (Trellis uses white #FFFFFF)
    'fill-field-default': surfaceVariants.subtle,
    'fill-field-default-alt': surfaceVariants.hover,

    // trellis-light's icon-inverse-default (#141414) is correct for dark icons on light surfaces,
    // but deriveTertiaryColorVariants sets it to getBestTextColor(accentColor) which is white for
    // dark accent colors — invisible on a white modal body. Override to near-black so the Modal
    // close icon is always visible in light mode regardless of the brand's accent color.
    'icon-inverse-default': '#141414',
    // text-inverse-default is the Dialog/PanelHeader title text color, rendered on
    // the modal header gradient. In light mode the gradient is not overridden, so
    // it stays on the white surface — derive the title text from the light surface
    // (yields dark text). Trellis light base value: #FFFFFF.
    'text-inverse-default': getBestTextColor(TRELLIS_LIGHT_SURFACE_DEFAULT),
  };
}

export function deriveTextInteractiveDefaultColorVariants(
  baseColor: string
): TextInteractiveColorTokens & FwcTextInteractiveColorTokens {
  const variants = deriveColorVariants(baseColor);

  const baseTokens = {
    'text-interactive-default': variants.default,
    'text-interactive-default-hover': variants.hover,
    'text-interactive-default-pressed': variants.pressed,
    'specialty-text-interactive-alt-default': variants.default,
    'specialty-text-interactive-alt-hover': variants.hover,
    'specialty-text-interactive-alt-pressed': variants.pressed,
    'border-interactive-default': variants.default,
    'specialty-text-core-alt-default': variants.default,
  } satisfies TextInteractiveColorTokens;

  return {
    ...baseTokens,

    // fwc-link tokens used by Button use="transparent" and Link components
    'fwc-link-specialty-text-interactive-alt-default': variants.default,
    'fwc-link-specialty-text-interactive-alt-hover': variants.hover,
    'fwc-link-specialty-text-interactive-alt-pressed': variants.pressed,
    'fwc-link-specialty-text-core-alt-default': variants.default,
    'fwc-link-text-interactive-default': variants.default,
    'fwc-link-text-interactive-default-hover': variants.hover,
    'fwc-link-text-interactive-default-pressed': variants.pressed,

    // fwc-button equivalents
    'fwc-button-specialty-text-interactive-alt-default': variants.default,
    'fwc-button-specialty-text-interactive-alt-hover': variants.hover,
    'fwc-button-specialty-text-interactive-alt-pressed': variants.pressed,
    'fwc-button-specialty-text-core-alt-default': variants.default,

    // Link underline colors
    'fwc-link-underline-default': variants.default,
    'fwc-link-underline-hover': variants.hover,
    'fwc-link-underline-pressed': variants.pressed,
    'fwc-link-underline-disabled': variants.disabled,

    // Link alert text
    'fwc-link-text-alert-default': variants.default,

    // LoadingSpinner link variant
    'fwc-loadingspinner-ring-link': variants.default,
    'fwc-loadingspinner-indicator-link': variants.default,
  };
}

export function deriveColorFillCautionDefaultVariants(
  baseColor: string
): CautionColorTokens & FwcCautionColorTokens {
  const variants = deriveColorVariants(baseColor);
  const alertSubtle = deriveAlertSubtleColor(baseColor);

  const baseTokens = {
    // status tags
    'fill-caution-default': variants.default,
    'fill-caution-hover': variants.hover,
    'fill-caution-pressed': variants.pressed,
    'fill-accent-yellow-subtle-alt': alertSubtle,
    'fill-caution-subtle': alertSubtle,
    // tag components
    'border-caution-default': alertSubtle,
  } satisfies CautionColorTokens;

  return {
    ...baseTokens,
    'fwc-badge-fill-marigold': variants.default,
    'fwc-badge-border-marigold': variants.default,
    'fwc-ribbon-fill-marigold': variants.default,
    'fwc-tag-fill-marigold': alertSubtle,
    'fwc-tag-border-marigold': alertSubtle,
    'fwc-tag-fill-badge-marigold': variants.default,
    'fwc-tag-border-badge-marigold': alertSubtle,
    'fwc-tag-fill-yellow': alertSubtle,
    'fwc-tag-border-yellow': alertSubtle,
    'fwc-tag-fill-badge-yellow': alertSubtle,
    'fwc-tag-border-badge-yellow': alertSubtle,
  };
}

export function deriveColorFillAccentGreenDefaultVariants(
  baseColor: string
): AccentGreenColorTokens & FwcAccentGreenColorTokens {
  const variants = deriveColorVariants(baseColor);
  const alertSubtle = deriveAlertSubtleColor(baseColor);

  const baseTokens = {
    // status tags
    'fill-accent-green-default': variants.default,
    'fill-accent-green-subtle': variants.subtle,
    'fill-accent-green-subtle-alt': alertSubtle,
    'fill-accent-green-pressed': variants.pressed,
    'fill-accent-green-hover': variants.hover,
    // tag components
    'border-accent-green-default': alertSubtle,
    'border-positive-default': alertSubtle,
    'fill-positive-subtle': alertSubtle,
  } satisfies AccentGreenColorTokens;

  return {
    ...baseTokens,
    'fwc-badge-fill-oz': variants.default,
    'fwc-badge-border-oz': variants.default,
    'fwc-badge-fill-new': variants.default,
    'fwc-badge-border-new': variants.default,
    'fwc-ribbon-fill-oz': variants.default,
    'fwc-ribbon-fill-new': variants.default,
    'fwc-tag-fill-oz': alertSubtle,
    'fwc-tag-border-oz': alertSubtle,
    'fwc-tag-fill-badge-oz': variants.default,
    'fwc-tag-border-badge-oz': alertSubtle,
    'fwc-tag-fill-green': variants.subtle,
    'fwc-tag-border-green': alertSubtle,
    'fwc-tag-fill-badge-green': variants.subtle,
    'fwc-tag-border-badge-green': alertSubtle,
    'fwc-loadingspinner-indicator-success': variants.default,
    'fwc-loadingspinner-ring-success': variants.disabled,
  };
}

export function deriveColorFillAccentRedDefaultVariants(
  baseColor: string
): AccentRedColorTokens & FwcAccentRedColorTokens {
  const variants = deriveColorVariants(baseColor);
  const alertSubtle = deriveAlertSubtleColor(baseColor);
  const textColor = getBestTextColor(baseColor);
  const subtleTextColor = getBestTextColor(alertSubtle);

  const baseTokens = {
    // status tags
    'fill-accent-red-default': variants.default,
    'fill-accent-red-subtle': variants.subtle,
    'fill-accent-red-subtle-alt': alertSubtle,
    'fill-accent-red-pressed': variants.pressed,
    'fill-accent-red-hover': variants.hover,
    'fill-alert-subtle': alertSubtle,
    'fill-transitional-alert': variants.default,
    'border-alert-default': alertSubtle,
    // text
    'text-alert-default': subtleTextColor,
    'text-alert-on-fill-default': textColor,
  } satisfies AccentRedColorTokens;

  return {
    ...baseTokens,
    'fwc-button-fill-alert-default': variants.default,
    'fwc-button-fill-alert-hover': variants.hover,
    'fwc-button-fill-alert-pressed': variants.pressed,
    'fwc-button-text-alert-on-fill-default': textColor,
    'fwc-badge-fill-candy-apple': variants.default,
    'fwc-badge-border-candy-apple': variants.default,
    'fwc-ribbon-fill-candy-apple': variants.default,
    'fwc-tag-fill-candy-apple': alertSubtle,
    'fwc-tag-border-candy-apple': variants.default,
    'fwc-tag-fill-badge-candy-apple': variants.default,
    'fwc-tag-border-badge-candy-apple': variants.default,
    'fwc-badge-fill-red': variants.subtle,
    'fwc-badge-border-red': variants.subtle,
    'fwc-tag-fill-red': variants.subtle,
    'fwc-tag-border-red': variants.default,
    'fwc-tag-fill-badge-red': variants.subtle,
    'fwc-tag-border-badge-red': variants.default,
    'fwc-loadingspinner-indicator-danger': variants.default,
    'fwc-loadingspinner-ring-danger': variants.disabled,
  };
}

export function deriveColorFillInfoDefaultVariants(
  baseColor: string
): InfoColorTokens & FwcInfoColorTokens {
  const variants = deriveColorVariants(baseColor);
  const alertSubtle = deriveAlertSubtleColor(baseColor);

  const baseTokens = {
    'fill-info-default': variants.default,
    'fill-info-subtle': alertSubtle,
    'fill-info-hover': variants.hover,
    'fill-info-pressed': variants.pressed,
    'border-info-default': alertSubtle,
    'border-transitional-interactive-subtle': variants.default,
    'fill-accent-blue-default': variants.default,
    'fill-accent-blue-subtle': variants.subtle,
  } satisfies InfoColorTokens;

  return {
    ...baseTokens,
    'fwc-alert-border-default': alertSubtle,
    'fwc-badge-fill-calypso': variants.default,
    'fwc-badge-border-calypso': variants.default,
    'fwc-ribbon-fill-calypso': variants.default,
    'fwc-tag-fill-badge-calypso': variants.default,
    'fwc-tag-border-badge-calypso': variants.default,
    'fwc-tag-fill-blue': variants.subtle,
    'fwc-tag-border-blue': variants.default,
    'fwc-tag-fill-badge-blue': variants.subtle,
    'fwc-tag-border-badge-blue': variants.default,
  };
}

function deriveAccentOrangeFwcTokens(
  baseColor: string
): FwcAccentOrangeColorTokens {
  const variants = deriveColorVariants(baseColor);
  const alertSubtle = deriveAlertSubtleColor(baseColor);
  return {
    'fwc-badge-fill-lorax': variants.default,
    'fwc-badge-border-lorax': variants.default,
    'fwc-badge-fill-free': variants.default,
    'fwc-badge-border-free': variants.default,
    'fwc-badge-fill-sorbet': variants.default,
    'fwc-badge-border-sorbet': variants.default,
    'fwc-ribbon-fill-lorax': variants.default,
    'fwc-ribbon-fill-free': variants.default,
    'fwc-ribbon-fill-sorbet': variants.default,
    'fwc-tag-fill-lorax': alertSubtle,
    'fwc-tag-border-lorax': variants.default,
    'fwc-tag-fill-badge-lorax': variants.default,
    'fwc-tag-border-badge-lorax': variants.default,
    'fwc-tag-fill-sorbet': alertSubtle,
    'fwc-tag-border-sorbet': variants.default,
    'fwc-tag-fill-badge-sorbet': variants.default,
    'fwc-tag-border-badge-sorbet': variants.default,
    'fwc-tag-fill-orange': alertSubtle,
    'fwc-tag-border-orange': alertSubtle,
    'fwc-tag-fill-badge-orange': alertSubtle,
    'fwc-tag-border-badge-orange': variants.default,
  };
}

function deriveAccentPurpleFwcTokens(
  baseColor: string
): FwcAccentPurpleColorTokens {
  const variants = deriveColorVariants(baseColor);
  const alertSubtle = deriveAlertSubtleColor(baseColor);
  return {
    'fwc-badge-fill-thunderdome': variants.default,
    'fwc-badge-border-thunderdome': variants.default,
    'fwc-badge-fill-beta': variants.default,
    'fwc-badge-border-beta': variants.default,
    'fwc-ribbon-fill-thunderdome': variants.default,
    'fwc-ribbon-fill-beta': variants.default,
    'fwc-tag-fill-thunderdome': alertSubtle,
    'fwc-tag-border-thunderdome': variants.default,
    'fwc-tag-fill-badge-thunderdome': variants.default,
    'fwc-tag-border-badge-thunderdome': variants.default,
    'fwc-tag-fill-purple': alertSubtle,
    'fwc-tag-border-purple': variants.default,
    'fwc-tag-fill-badge-purple': alertSubtle,
    'fwc-tag-border-badge-purple': variants.default,
  };
}

interface TokenConfig {
  variants: VariantSet;
  derivedTokens?: {
    text?: string;
    border?: string;
    icon?: string;
  };
}

const tokenConfigMap = new Map<string, TokenConfig>();

for (const config of TOKEN_VARIANT_CONFIGS) {
  for (const prefix of config.prefixes) {
    tokenConfigMap.set(prefix, {
      variants: config.variants,
      derivedTokens: config.derivedTokens,
    });
  }
}

function getConfigForTokenPrefix(tokenPrefix: string): TokenConfig {
  const exactMatch = tokenConfigMap.get(tokenPrefix);
  if (exactMatch) {
    return exactMatch;
  }

  return { variants: SINGLE_VARIANT };
}

// Derives all variant tokens for a given base color and target token.
export function deriveGenericColorVariants(
  baseColor: string,
  targetToken: AvailableTokens
): Record<string, string> {
  const tokenPrefix = targetToken.replace(
    /-(default|subtle|hover|pressed)$/,
    ''
  );
  const config = getConfigForTokenPrefix(tokenPrefix);

  if (config.variants.length === 0) {
    return { [targetToken]: baseColor };
  }

  const colorVariants = deriveColorVariants(baseColor);
  const result: Record<string, string> = {};

  for (const variant of config.variants) {
    const variantKey = variant as keyof typeof colorVariants;
    if (colorVariants[variantKey]) {
      result[`${tokenPrefix}-${variant}`] = colorVariants[variantKey];
    }
  }

  if (config.derivedTokens?.text) {
    const textColor = getBestTextColor(baseColor);
    result[`${config.derivedTokens.text}-default`] = textColor;
  }

  if (config.derivedTokens?.border) {
    result[`${config.derivedTokens.border}-default`] = baseColor;
  }

  if (config.derivedTokens?.icon) {
    const iconColor = getBestTextColor(baseColor);
    result[`${config.derivedTokens.icon}-default`] = iconColor;
  }

  return result;
}

export const COLOR_OVERRIDE_CONFIGS: Record<
  ProtectedTopLevelColorTokens,
  {
    brandField: BrandFields;
    themeSettingField: ProtectedColorThemeSettingFields | null;
    deriveVariants: (color: string) => Record<string, string>;
  }
> = {
  'fill-primary-default': {
    brandField: 'primaryColor',
    themeSettingField: 'trellisColorFillPrimaryDefault',
    deriveVariants: derivePrimaryColorVariants,
  },
  'fill-secondary-default': {
    brandField: 'secondaryColor',
    themeSettingField: 'trellisColorFillSecondaryDefault',
    deriveVariants: deriveSecondaryColorVariants,
  },
  'fill-tertiary-default': {
    brandField: 'accentColor1',
    themeSettingField: 'trellisColorFillTertiaryDefault',
    deriveVariants: deriveTertiaryColorVariants,
  },
  'text-interactive-default': {
    brandField: null,
    themeSettingField: 'trellisColorTextInteractiveDefault',
    deriveVariants: deriveTextInteractiveDefaultColorVariants,
  },
  // The below configs are not customizable via CMS theme fields
  // but necessary to allow us to derive the necessary variants for
  // the default token values we set for the base color token keys
  // (e.g. fill-caution-default, fill-accent-green-default, etc)
  'fill-caution-default': {
    brandField: null,
    themeSettingField: null,
    deriveVariants: deriveColorFillCautionDefaultVariants,
  },
  'fill-accent-green-default': {
    brandField: null,
    themeSettingField: null,
    deriveVariants: deriveColorFillAccentGreenDefaultVariants,
  },
  'fill-accent-red-default': {
    brandField: null,
    themeSettingField: null,
    deriveVariants: deriveColorFillAccentRedDefaultVariants,
  },
  'fill-info-default': {
    brandField: null,
    themeSettingField: null,
    deriveVariants: deriveColorFillInfoDefaultVariants,
  },
  // Accent colors used by Badge (free, beta, count, dot variants).
  // Uses deriveGenericColorVariants which produces fill variants (default, hover, pressed, subtle)
  // based on TOKEN_VARIANT_CONFIGS (COMPLETE_VARIANT).
  'fill-accent-orange-default': {
    brandField: null,
    themeSettingField: null,
    deriveVariants: (color: string) => ({
      ...deriveGenericColorVariants(color, 'fill-accent-orange-default'),
      ...deriveAccentOrangeFwcTokens(color),
    }),
  },
  'fill-accent-purple-default': {
    brandField: null,
    themeSettingField: null,
    deriveVariants: (color: string) => ({
      ...deriveGenericColorVariants(color, 'fill-accent-purple-default'),
      ...deriveAccentPurpleFwcTokens(color),
    }),
  },
} as const;

export const SURFACE_STYLE_OVERRIDE_CONFIG: {
  themeSettingField: 'trellisColorFillSurfaceDefault';
  deriveVariants: (
    style: StyleField
  ) => Partial<
    FillSurfaceColorTokens & FwcDarkSurfaceTokens & FwcTableSurfaceTokens
  >;
} = {
  themeSettingField: 'trellisColorFillSurfaceDefault',
  deriveVariants: deriveSurfaceTokensFromStyle,
};

// Borders
export function deriveBorderRadiusVariants(
  borderRadiusChoice: string
): BorderRadiusTokens & FwcBorderRadiusTokens {
  if (borderRadiusChoice === 'rounded') {
    const baseTokens = {
      '0': '0px',
      '100': '4px',
      '200': '4px',
      '300': '8px',
      '400': '16px',
      full: '999999px',
      'transitional-container': '8px',
      'transitional-container-alt': '8px',
      'transitional-component': '4px',
      'transitional-floating': '16px',
      'transitional-full-0': '999999px',
      'transitional-full-3': '999999px',
      'transitional-full-8': '999999px',
    } satisfies BorderRadiusTokens;

    return {
      ...baseTokens,
      'fwc-button': '4px',
      'fwc-progress': '999999px',
      'fwc-alert': '16px',
      'fwc-tag': '6px',
      'fwc-tag-badge': '6px',
      'fwc-select-options-container': '16px',
      'fwc-dropdown-options-container': '16px',
      'fwc-expandabletext-scrollable-container': '16px',
    };
  } else if (borderRadiusChoice === 'pill') {
    const baseTokens = {
      '0': '16px',
      '100': '999999px',
      '200': '999999px',
      '300': '999999px',
      '400': '999999px',
      full: '999999px',
      'transitional-container': '16px',
      'transitional-container-alt': '16px',
      'transitional-component': '8px',
      'transitional-floating': '16px',
      'transitional-full-0': '999999px',
      'transitional-full-3': '999999px',
      'transitional-full-8': '999999px',
    } satisfies BorderRadiusTokens;

    return {
      ...baseTokens,
      'fwc-button': '999999px',
      'fwc-progress': '999999px',
      'fwc-alert': '16px',
      'fwc-tag': '999999px',
      'fwc-tag-badge': '999999px',
      'fwc-select-options-container': '16px',
      'fwc-dropdown-options-container': '16px',
      'fwc-expandabletext-scrollable-container': '16px',
    };
  } else {
    const baseTokens = {
      '0': '0px',
      '100': '0px',
      '200': '0px',
      '300': '0px',
      '400': '0px',
      full: '999999px',
      'transitional-container': '0px',
      'transitional-container-alt': '0px',
      'transitional-component': '0px',
      'transitional-floating': '0px',
      'transitional-full-0': '0px',
      'transitional-full-3': '0px',
      'transitional-full-8': '0px',
    } satisfies BorderRadiusTokens;

    return {
      ...baseTokens,
      'fwc-button': '0px',
      'fwc-progress': '0px',
      'fwc-alert': '0px',
      'fwc-tag': '0px',
      'fwc-tag-badge': '0px',
      'fwc-select-options-container': '0px',
      'fwc-dropdown-options-container': '0px',
      'fwc-expandabletext-scrollable-container': '0px',
    };
  }
}

export const BORDER_RADIUS_OVERRIDE_CONFIGS: {
  themeSettingField: BorderRadiusThemeSettingFields;
  deriveVariants: (
    borderRadiusChoice: string
  ) => BorderRadiusTokens & FwcBorderRadiusTokens;
}[] = [
  {
    themeSettingField: 'trellisBorderRadius',
    deriveVariants: deriveBorderRadiusVariants,
  },
];

export function buildFontFamilyString(
  fontName: string,
  fallback = 'sans-serif'
): string {
  return `${fontName}, ${fallback}`;
}

export function resolveThemeFontFamily(font: FontFieldValue): string {
  if (!font.font) {
    return '';
  }

  return buildFontFamilyString(font.font, font.fallback || undefined);
}

// Fonts
const BODY_TYPOGRAPHY_TOKENS: (keyof ThemeTypographySubset)[] = [
  'body-100',
  'body-125',
  'body-200',
  'body-300',
  'body-400',
  'body-500',
  'body-600',
  'body-700',
  'detail-100',
  'detail-200',
  'link-25',
  'link-100',
  'link-200',
];

const HEADING_TYPOGRAPHY_TOKENS: (keyof ThemeTypographySubset)[] = [
  'heading-25',
  'heading-50',
  'heading-100',
  'heading-200',
  'heading-300',
  'heading-400',
  'heading-500',
  'heading-600',
  'heading-700',
  'heading-800',
  'heading-900',
  'heading-1000',
  'display-100',
  'display-200',
  'display-300',
  'display-400',
  'display-500',
];

function deriveFontVariants(
  fontFamily: string,
  tokens: readonly (keyof ThemeTypographySubset)[]
): Record<string, Partial<{ fontFamily: string }>> {
  const typographyOverrides: Record<
    string,
    Partial<{ fontFamily: string }>
  > = {};

  tokens.forEach((token) => {
    typographyOverrides[token as string] = {
      fontFamily,
    };
  });

  return typographyOverrides;
}

export function deriveBodyFontVariants(
  fontFamily: string
): Record<string, Partial<{ fontFamily: string }>> {
  return deriveFontVariants(fontFamily, BODY_TYPOGRAPHY_TOKENS);
}

export function deriveHeadingFontVariants(
  fontFamily: string
): Record<string, Partial<{ fontFamily: string }>> {
  return deriveFontVariants(fontFamily, HEADING_TYPOGRAPHY_TOKENS);
}

export const FONT_OVERRIDE_CONFIGS: Record<
  'body' | 'heading',
  {
    brandField: FontFields;
    themeSettingField: ProtectedFontThemeSettingFields;
    deriveVariants: (
      fontFamily: string
    ) => Record<string, Partial<{ fontFamily: string }>>;
  }
> = {
  body: {
    brandField: 'bodyFont',
    themeSettingField: 'bodyFont',
    deriveVariants: deriveBodyFontVariants,
  },
  heading: {
    brandField: 'primaryFont',
    themeSettingField: 'primaryFont',
    deriveVariants: deriveHeadingFontVariants,
  },
} as const;
